import { useEffect, useRef, useState } from "react";
import {
  SESSIONS_ALL_MAX_PAGES,
  accountScope,
  SessionResult,
  buildCursorPath,
  fetchCatalogExercises,
  onForeground,
  onReconnect,
  errorCodeOf,
  paginate,
  startedAtMsOf,
  type Exercise,
  type ExerciseCatalogReader,
  type TimeRange,
  type TimedSessionResult,
} from "@heraion/shared";

import type {
  PersistResults,
  ProgressApiFetch,
  ProgressDeps,
  SessionResultsCacheModule,
  SessionResultsSnapshot,
} from "./types";

type ListResp = { items?: unknown[]; cursor?: string | null };
type Held = { snap: SessionResultsSnapshot; cache: SessionResultsCacheModule };

// The server's page cap (apps/api/src/routes/sessionResults.ts).
export const RESULTS_PAGE_SIZE = 200;

/** PG-64: every row of a period, following the cursor. The newest-200 read
 *  below stays a single page: it is a recent window, not a period. */
async function fetchRangeRows(
  api: ProgressApiFetch,
  range: TimeRange,
): Promise<unknown[]> {
  const query = `since=${Math.floor(range.fromMs)}&until=${Math.floor(range.toMs)}&limit=${RESULTS_PAGE_SIZE}`;
  const { items } = await paginate<unknown, string>(
    async (cursor) => {
      const res = await api<ListResp>(
        buildCursorPath("/api/session-results", query, cursor),
      );
      return { items: res.items ?? [], cursor: res.cursor ?? null };
    },
    { maxPages: SESSIONS_ALL_MAX_PAGES },
  );
  return items;
}

// Offline with an expired window: the cached rows beat no catalog at all.
async function loadCatalog(
  source: ExerciseCatalogReader,
): Promise<ReadonlyArray<Exercise>> {
  try {
    return await source.fetchAndPersist();
  } catch (err) {
    const cached = await source.loadCached();
    if (cached) return cached;
    throw err;
  }
}

// Same union the rest of the app already speaks (useAsyncResource.ts,
// usePlans, usePrograms, useAthleteTargets). "refreshing" is the state
// this hook was missing: we HAVE numbers, but they came off the disk and
// the network has not confirmed them yet. Collapsing it into "loaded" is
// what let the coverage card show a stale set count as if it were final.
export type SessionResultsStatus =
  | "loading"
  | "loaded"
  | "refreshing"
  | "error";

export type UseSessionResultsOptions = {
  apiFetch: ProgressApiFetch;
  /** See ProgressDeps.catalog: absent means an uncached read via apiFetch. */
  catalog?: ExerciseCatalogReader;
  // Shared single-flight instance (host-owned, so the save path
  // invalidates the same one the panels subscribe to).
  cache: SessionResultsCacheModule;
  // Optional cold-boot disk read (mobile AsyncStorage, web localStorage).
  readStale?: () => Promise<SessionResultsSnapshot | null>;
  // Optional best-effort persist of parsed results for the next cold boot.
  persistResults?: PersistResults;
  // PG-64: read this whole period instead of the newest 200 rows. Pair it
  // with a cache of its own: the cache holds one snapshot.
  range?: TimeRange;
};

// Shared data hook for the Progress panels. ONE /api/session-results page
// (a report's period: every page of it) + the catalog, parsed ONCE and
// shared across every consumer via the injected single-flight cache. Mirrors the mobile hook; the AsyncStorage
// SWR is now an injected concern (readStale / persistResults) so the hook
// stays platform-agnostic.
export function useSessionResults(opts: UseSessionResultsOptions): {
  results: TimedSessionResult[] | null;
  catalog: ReadonlyArray<import("@heraion/shared").Exercise> | null;
  error: string | null;
  status: SessionResultsStatus;
  /** The rows are this cache's (its read, or its disk copy), not the ones a
   *  previous cache left on screen while this one loads. */
  current: boolean;
} {
  const { cache } = opts;
  // Refs so a host passing fresh literals per render can't re-trigger the
  // mount effect (the unstable-object-dep lesson).
  const apiRef = useRef(opts.apiFetch);
  const catalogRef = useRef(opts.catalog);
  const staleRef = useRef(opts.readStale);
  const persistRef = useRef(opts.persistResults);
  const rangeRef = useRef(opts.range);
  useEffect(() => {
    apiRef.current = opts.apiFetch;
    catalogRef.current = opts.catalog;
    staleRef.current = opts.readStale;
    persistRef.current = opts.persistResults;
    rangeRef.current = opts.range;
  });

  // Each snapshot with the cache it belongs to (`current`, #397).
  const [held, setHeld] = useState<Held | null>(() => {
    const warm = cache.getCached();
    return warm ? { snap: warm, cache } : null;
  });
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<SessionResultsStatus>(
    cache.getCached() ? "loaded" : "loading",
  );

  useEffect(() => {
    let cancelled = false;

    const fetchSnapshot = async (): Promise<SessionResultsSnapshot> => {
      // #398: the account this read is for; the host's copy is written under it.
      const scope = accountScope();
      const api = apiRef.current;
      const range = rangeRef.current;
      const [rows, catalog] = await Promise.all([
        range
          ? fetchRangeRows(api, range)
          : api<ListResp>(`/api/session-results?limit=${RESULTS_PAGE_SIZE}`).then(
              (r) => r.items ?? [],
            ),
        catalogRef.current
          ? loadCatalog(catalogRef.current)
          : fetchCatalogExercises(api),
      ]);
      const results: TimedSessionResult[] = [];
      for (const raw of rows) {
        const p = SessionResult.safeParse(raw);
        if (!p.success) continue;
        results.push({ result: p.data, startedAtMs: startedAtMsOf(raw, p.data) });
      }
      // #469: the count before the drop, or a full page with one bad row
      // would read as the whole history.
      persistRef.current?.(results, scope, rows.length);
      return { results, catalog, rawCount: rows.length };
    };

    const refresh = () => {
      // A refresh over numbers already on screen is "refreshing", never
      // "loading" — the caller must keep painting them meanwhile.
      setStatus((prev) => (prev === "loading" ? "loading" : "refreshing"));
      cache
        .ensure(fetchSnapshot)
        .then((s) => {
          if (!cancelled) {
            setHeld({ snap: s, cache });
            setError(null);
            setStatus("loaded");
          }
        })
        .catch((e) => {
          if (!cancelled) {
            setError(errorCodeOf(e));
            // Deliberately NOT clearing snap: a failed refresh must not
            // erase what we already hold. The caller decides whether to
            // keep showing it, flagged as unconfirmed.
            setStatus("error");
          }
        });
    };

    // Cold boot (no warm in-memory cache): paint the last persisted
    // snapshot immediately so the panels skip the network-length spinner.
    // Never overwrites a fresher snapshot the refresh already delivered.
    if (!cache.getCached() && staleRef.current) {
      void staleRef.current().then((stale) => {
        if (cancelled || stale === null) return;
        // Over another cache's rows too: these are this cache's own.
        setHeld((cur) => (cur?.cache === cache ? cur : { snap: stale, cache }));
        // Numbers are on screen now, but they are the disk's, not the
        // server's: that is exactly "refreshing", not "loaded".
        setStatus((prev) => (prev === "loading" ? "refreshing" : prev));
      });
    }
    const unsubscribe = cache.subscribe(() => {
      if (cancelled) return;
      setHeld(null);
      setStatus("loading");
      refresh();
    });
    // #397: back online, a read that failed reads again (single-flight).
    const offReconnect = onReconnect(() => {
      if (!cancelled && !cache.getCached()) refresh();
    });
    // #509: back in front, a snapshot past its expiry reads again (`ensure`
    // answers a fresh one at once, without a request).
    const offForeground = onForeground(() => {
      if (!cancelled && (!cache.getCached() || cache.isStale?.() === true)) refresh();
    });
    refresh();
    return () => {
      cancelled = true;
      unsubscribe();
      offReconnect();
      offForeground();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cache]);

  return {
    results: held?.snap.results ?? null,
    catalog: held?.snap.catalog ?? null,
    error,
    status,
    current: held?.cache === cache,
  };
}

// Convenience wrapper — every Tier-1 panel shares one set of ProgressDeps,
// so they all call this instead of re-spreading the same four fields.
export function useSessionResultsFromDeps(deps: ProgressDeps) {
  return useSessionResults({
    apiFetch: deps.apiFetch,
    catalog: deps.catalog,
    cache: deps.resultsCache,
    readStale: deps.readStaleResults,
    persistResults: deps.persistResults,
    range: deps.resultsRange,
  });
}
